// src/components/grammarQuest/SentenceBuilderChallenge.jsx
// Reto Final — componer una frase con al menos 3 palabras coleccionadas
import React, { useState, useCallback } from 'react';
import { X, RotateCcw, CheckCircle, Sparkles, Volume2, Trophy } from 'lucide-react';
import Mascot from '../Mascot';

function speak(text) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = 'en-US'; utt.rate = 0.85;
  window.speechSynthesis.speak(utt);
}

// Partículas de confeti
function ConfettiParticle({ i }) {
  const colors = ['#a78bfa', '#f59e0b', '#34d399', '#60a5fa', '#f472b6', '#fbbf24'];
  const color = colors[i % colors.length];
  const left = `${5 + (i * 9.3) % 90}%`;
  const delay = `${(i * 0.13) % 1.2}s`;
  const size = 6 + (i % 4) * 3;
  return (
    <div
      className="absolute top-0 rounded-sm animate-gwq-confetti pointer-events-none"
      style={{ left, width: size, height: size, background: color, animationDelay: delay, opacity: 0 }}
    />
  );
}

export default function SentenceBuilderChallenge({
  collection,
  onClose,
  onReset,
  activeSkin,
  level,
}) {
  const MIN_WORDS = 3;
  const [usedWords, setUsedWords] = useState([]); // palabras seleccionadas en el lienzo
  const [phase, setPhase] = useState('building'); // 'building' | 'success'

  const toggleWord = useCallback((word) => {
    setUsedWords(prev =>
      prev.includes(word) ? prev.filter(w => w !== word) : [...prev, word]
    );
  }, []);

  const handleCheck = useCallback(() => {
    if (usedWords.length < MIN_WORDS) return;
    setPhase('success');
    speak(usedWords.join(' '));
  }, [usedWords]);

  const handleReset = useCallback(() => {
    setUsedWords([]);
    setPhase('building');
  }, []);

  const canCheck = usedWords.length >= MIN_WORDS;
  const builtSentence = usedWords.join(' ');

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(15,10,40,0.7)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="w-full max-w-md rounded-t-[2.5rem] overflow-hidden animate-in slide-in-from-bottom-5 duration-400 flex flex-col"
        style={{
          maxHeight: '92vh',
          background: 'linear-gradient(160deg, #1e1b4b 0%, #2e1065 100%)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-2 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center text-xl">
              🏆
            </div>
            <div>
              <h2 className="text-white font-black text-base leading-tight">Reto Final</h2>
              <p className="text-violet-300 text-[10px] font-bold uppercase tracking-widest">Sentence Builder · {level}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-full bg-white/10 border border-white/10 text-white/60 hover:bg-white/20 transition-all active:scale-90"
          >
            <X size={18} />
          </button>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-5">

          {phase === 'building' && (
            <>
              {/* Mascota */}
              <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl p-3">
                <div className="shrink-0">
                  <Mascot type={activeSkin} />
                </div>
                <div className="flex-1 min-w-0 pt-1">
                  <p className="text-white/90 font-bold text-sm leading-relaxed">
                    ¡Increíble trabajo! Ahora usa al menos{' '}
                    <span className="text-amber-300 font-black">{MIN_WORDS} palabras</span>{' '}
                    de tu colección para construir una frase en inglés.
                  </p>
                  <p className="text-violet-300 text-[11px] mt-1.5">
                    Toca las palabras para añadirlas al lienzo.
                  </p>
                </div>
              </div>

              {/* Lienzo de construcción */}
              <div>
                <p className="text-violet-300 text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <Sparkles size={11} /> Tu frase
                  <span className="text-violet-500">({usedWords.length}/{MIN_WORDS} min.)</span>
                </p>
                <div
                  className="min-h-[72px] rounded-2xl border-2 border-dashed flex flex-wrap gap-2 p-3 items-start transition-all duration-300"
                  style={{
                    borderColor: canCheck ? 'rgba(167,139,250,0.6)' : 'rgba(255,255,255,0.15)',
                    background: canCheck ? 'rgba(167,139,250,0.08)' : 'rgba(255,255,255,0.03)',
                  }}
                >
                  {usedWords.length === 0 ? (
                    <p className="text-white/20 text-sm font-medium w-full text-center self-center">
                      Selecciona palabras abajo ↓
                    </p>
                  ) : (
                    usedWords.map((word, i) => (
                      <button
                        key={i}
                        onClick={() => toggleWord(word)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-violet-500 text-white text-sm font-bold border border-violet-400 hover:bg-violet-600 active:scale-95 transition-all shadow-md"
                      >
                        {word}
                        <X size={12} className="opacity-70" />
                      </button>
                    ))
                  )}
                </div>

                {/* Vista previa de la frase */}
                {builtSentence && (
                  <div className="mt-2 flex items-center gap-2">
                    <p className="text-violet-200 text-xs font-semibold italic flex-1">"{builtSentence}"</p>
                    <button
                      onClick={() => speak(builtSentence)}
                      className="p-1.5 rounded-lg bg-violet-500/20 border border-violet-400/30 text-violet-300 hover:bg-violet-500/30 transition-all active:scale-90"
                    >
                      <Volume2 size={13} />
                    </button>
                  </div>
                )}
              </div>

              {/* Palabras disponibles */}
              <div>
                <p className="text-violet-300 text-[10px] font-black uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  📚 Tu colección
                </p>
                <div className="flex flex-wrap gap-2">
                  {collection.map((word, i) => {
                    const isUsed = usedWords.includes(word);
                    return (
                      <button
                        key={i}
                        onClick={() => toggleWord(word)}
                        className={`px-3 py-2 rounded-xl text-sm font-bold border transition-all active:scale-95 ${
                          isUsed
                            ? 'bg-violet-500 border-violet-400 text-white shadow-md shadow-violet-500/30'
                            : 'bg-white/10 border-white/20 text-white/80 hover:bg-white/20'
                        }`}
                      >
                        {isUsed ? '✓ ' : ''}{word}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex gap-3">
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-white/10 border border-white/15 text-white/70 font-bold text-sm hover:bg-white/15 transition-all active:scale-95"
                >
                  <RotateCcw size={16} /> Limpiar
                </button>
                <button
                  onClick={handleCheck}
                  disabled={!canCheck}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-sm transition-all active:scale-95 border-b-4 ${
                    canCheck
                      ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 border-amber-600 hover:from-amber-300 shadow-lg shadow-amber-500/30'
                      : 'bg-white/10 border-white/10 text-white/30 cursor-not-allowed'
                  }`}
                >
                  <CheckCircle size={18} />
                  Comprobar frase
                </button>
              </div>
            </>
          )}

          {/* ── PANTALLA DE ÉXITO ── */}
          {phase === 'success' && (
            <div className="relative text-center space-y-5 py-4 overflow-hidden">
              {/* Confeti */}
              <div className="absolute inset-0 pointer-events-none">
                {Array.from({ length: 18 }).map((_, i) => <ConfettiParticle key={i} i={i} />)}
              </div>

              <div className="relative z-10 space-y-5">
                {/* Mascota celebrando */}
                <div className="flex justify-center">
                  <Mascot type={activeSkin} message="¡Eres increíble! 🏆" />
                </div>

                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/20 border border-amber-400/30 mb-3">
                    <Trophy size={16} className="text-amber-400" />
                    <span className="text-amber-300 font-black text-sm">¡Nivel A2 Completado!</span>
                  </div>
                  <p className="text-white font-black text-xl leading-tight">
                    ¡Felicitaciones! 🎉
                  </p>
                  <p className="text-violet-300 text-sm mt-1 font-medium">
                    Construiste tu propia frase en inglés
                  </p>
                </div>

                {/* La frase construida */}
                <div className="bg-white/10 border border-white/20 rounded-2xl p-4">
                  <p className="text-violet-300 text-[10px] font-black uppercase tracking-widest mb-2">Tu frase</p>
                  <p className="text-white font-black text-lg italic">"{builtSentence}"</p>
                  <button
                    onClick={() => speak(builtSentence)}
                    className="mt-3 flex items-center gap-2 mx-auto px-4 py-2 rounded-xl bg-violet-500/30 border border-violet-400/30 text-violet-200 text-sm font-bold hover:bg-violet-500/40 transition-all active:scale-95"
                  >
                    <Volume2 size={14} /> Escuchar
                  </button>
                </div>

                {/* Palabras usadas */}
                <div className="flex flex-wrap justify-center gap-2">
                  {usedWords.map((w, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-xl bg-amber-400/20 border border-amber-400/30 text-amber-300 text-xs font-bold">
                      ✓ {w}
                    </span>
                  ))}
                </div>

                {/* Botones finales */}
                <div className="flex gap-3">
                  <button
                    onClick={() => { handleReset(); }}
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white font-bold text-sm hover:bg-white/15 transition-all active:scale-95"
                  >
                    <RotateCcw size={16} /> Construir otra
                  </button>
                  <button
                    onClick={() => { onReset(); onClose(); }}
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-black text-sm border-b-4 border-purple-800 hover:from-violet-400 shadow-lg shadow-violet-500/30 transition-all active:scale-95 active:border-b-0"
                  >
                    <Sparkles size={16} /> Repetir nivel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
